import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LogOut, Home, Plus, Trash2, Save } from "lucide-react";

const EMPTY_ITEM = {
    category: "cafeteria",
    name_es: "",
    name_en: "",
    description_es: "",
    description_en: "",
    price: "",
    image_url: "",
    featured: false,
    order: 0,
    active: true,
};

const EMPTY_CAT = { slug: "", name_es: "", name_en: "", order: 0 };
const EMPTY_GAL = { url: "", caption_es: "", caption_en: "", order: 0 };

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { t, lang, toggle } = useI18n();
    const nav = useNavigate();
    const [tab, setTab] = useState("content");
    const [content, setContent] = useState(null);
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [gallery, setGallery] = useState([]);

    const loadAll = async () => {
        try {
            const [c, m, ca, g] = await Promise.all([
                api.get("/content"),
                api.get("/menu?only_active=false"),
                api.get("/categories"),
                api.get("/gallery"),
            ]);
            setContent(c.data);
            setItems(m.data);
            setCats(ca.data);
            setGallery(g.data);
        } catch (err) {
            toast.error("Error cargando datos");
        }
    };
    useEffect(() => {
        loadAll();
    }, []);

    const doLogout = async () => {
        await logout();
        nav("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-habana-ink">
            <header className="border-b border-gold/15 bg-habana-surface">
                <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="font-script text-gold text-xl">Habana</div>
                        <div className="font-serif text-habana-cream">{t("admin_title")}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-habana-cream/60 hidden md:block">{user?.email}</span>
                        <button
                            onClick={toggle}
                            className="px-3 py-1 rounded-full border border-gold/40 text-xs tracking-widest uppercase text-gold hover:bg-gold hover:text-black transition-colors"
                            data-testid="admin-dash-lang-toggle"
                        >
                            {lang === "es" ? "EN" : "ES"}
                        </button>
                        <Link to="/" className="btn-ghost-gold !py-2 !px-4 !text-xs" data-testid="admin-goto-site">
                            <Home size={14} /> {lang === "es" ? "Ver sitio" : "View site"}
                        </Link>
                        <button
                            onClick={doLogout}
                            className="btn-ghost-gold !py-2 !px-4 !text-xs"
                            data-testid="admin-logout-btn"
                        >
                            <LogOut size={14} /> {t("admin_logout")}
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-6 py-8">
                <div className="flex gap-2 mb-8 border-b border-gold/15 overflow-x-auto no-scrollbar">
                    <TabBtn active={tab === "content"} onClick={() => setTab("content")} testid="tab-content">
                        {t("admin_content_tab")}
                    </TabBtn>
                    <TabBtn active={tab === "categories"} onClick={() => setTab("categories")} testid="tab-categories">
                        {t("admin_categories_tab")}
                    </TabBtn>
                    <TabBtn active={tab === "menu"} onClick={() => setTab("menu")} testid="tab-menu">
                        {t("admin_menu_tab")}
                    </TabBtn>
                    <TabBtn active={tab === "gallery"} onClick={() => setTab("gallery")} testid="tab-gallery">
                        {t("admin_gallery_tab")}
                    </TabBtn>
                </div>

                {tab === "content" && content && (
                    <ContentPane content={content} onSave={loadAll} />
                )}
                {tab === "categories" && (
                    <CategoriesPane cats={cats} onChange={loadAll} />
                )}
                {tab === "menu" && (
                    <MenuPane items={items} cats={cats} onChange={loadAll} />
                )}
                {tab === "gallery" && (
                    <GalleryPane images={gallery} onChange={loadAll} />
                )}
            </div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-5 py-3 text-sm tracking-wide font-serif transition-colors border-b-2 ${
                active
                    ? "text-gold border-gold"
                    : "text-habana-cream/60 border-transparent hover:text-habana-cream"
            }`}
        >
            {children}
        </button>
    );
}

/* --------------------------------- Content -------------------------------- */
function ContentPane({ content, onSave }) {
    const { t, lang } = useI18n();
    const [form, setForm] = useState(content);
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/content", form);
            toast.success(lang === "es" ? "Contenido guardado" : "Content saved");
            onSave();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full bg-black/40 border border-gold/25 rounded-md px-3 py-2 text-habana-cream focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";
    const labelCls = "text-[0.7rem] tracking-widest uppercase text-gold/80";

    return (
        <div className="space-y-6" data-testid="content-pane">
            <SectionCard title={lang === "es" ? "Logo y Hero" : "Logo & Hero"}>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Logo URL"><input className={inputCls} value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} data-testid="content-logo-url" /></Field>
                    <Field label={lang === "es" ? "Fondo Hero URL" : "Hero background URL"}><input className={inputCls} value={form.hero_background_url || ""} onChange={(e) => update("hero_background_url", e.target.value)} data-testid="content-hero-bg" /></Field>
                    <Field label="Hero título (ES)"><input className={inputCls} value={form.hero_title_es || ""} onChange={(e) => update("hero_title_es", e.target.value)} data-testid="content-hero-title-es" /></Field>
                    <Field label="Hero title (EN)"><input className={inputCls} value={form.hero_title_en || ""} onChange={(e) => update("hero_title_en", e.target.value)} data-testid="content-hero-title-en" /></Field>
                    <Field label="Hero subtítulo (ES)"><textarea rows={2} className={inputCls} value={form.hero_subtitle_es || ""} onChange={(e) => update("hero_subtitle_es", e.target.value)} data-testid="content-hero-sub-es" /></Field>
                    <Field label="Hero subtitle (EN)"><textarea rows={2} className={inputCls} value={form.hero_subtitle_en || ""} onChange={(e) => update("hero_subtitle_en", e.target.value)} data-testid="content-hero-sub-en" /></Field>
                </div>
                {form.logo_url && (
                    <div className="mt-4">
                        <div className={labelCls}>Preview</div>
                        <img src={form.logo_url} alt="logo" className="mt-2 h-24 w-24 rounded-full ring-1 ring-gold/40 object-cover" />
                    </div>
                )}
            </SectionCard>

            <SectionCard title={lang === "es" ? "Nuestra Historia" : "Our Story"}>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Título (ES)"><input className={inputCls} value={form.about_title_es || ""} onChange={(e) => update("about_title_es", e.target.value)} data-testid="content-about-title-es" /></Field>
                    <Field label="Title (EN)"><input className={inputCls} value={form.about_title_en || ""} onChange={(e) => update("about_title_en", e.target.value)} data-testid="content-about-title-en" /></Field>
                    <Field label="Cuerpo (ES)"><textarea rows={4} className={inputCls} value={form.about_body_es || ""} onChange={(e) => update("about_body_es", e.target.value)} data-testid="content-about-body-es" /></Field>
                    <Field label="Body (EN)"><textarea rows={4} className={inputCls} value={form.about_body_en || ""} onChange={(e) => update("about_body_en", e.target.value)} data-testid="content-about-body-en" /></Field>
                </div>
            </SectionCard>

            <SectionCard title={lang === "es" ? "Contacto" : "Contact"}>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Teléfono principal"><input className={inputCls} value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="content-phone" /></Field>
                    <Field label="Teléfono secundario"><input className={inputCls} value={form.phone2 || ""} onChange={(e) => update("phone2", e.target.value)} data-testid="content-phone2" /></Field>
                    <Field label={lang === "es" ? "Dirección" : "Address"}><input className={inputCls} value={form.address || ""} onChange={(e) => update("address", e.target.value)} data-testid="content-address" /></Field>
                    <Field label="Horario (ES)"><input className={inputCls} value={form.hours_es || ""} onChange={(e) => update("hours_es", e.target.value)} data-testid="content-hours-es" /></Field>
                    <Field label="Hours (EN)"><input className={inputCls} value={form.hours_en || ""} onChange={(e) => update("hours_en", e.target.value)} data-testid="content-hours-en" /></Field>
                    <Field label="Instagram URL"><input className={inputCls} value={form.instagram || ""} onChange={(e) => update("instagram", e.target.value)} data-testid="content-instagram" /></Field>
                    <Field label="Facebook URL"><input className={inputCls} value={form.facebook || ""} onChange={(e) => update("facebook", e.target.value)} data-testid="content-facebook" /></Field>
                </div>
            </SectionCard>

            <button onClick={save} disabled={saving} className="btn-gold" data-testid="content-save-btn">
                <Save size={14} /> {saving ? "…" : t("admin_save")}
            </button>
        </div>
    );
}

/* -------------------------------- Categories ------------------------------ */
function CategoriesPane({ cats, onChange }) {
    const { t, lang } = useI18n();
    const [draft, setDraft] = useState(EMPTY_CAT);
    const [editingId, setEditingId] = useState(null);
    const inputCls = "w-full bg-black/40 border border-gold/25 rounded-md px-3 py-2 text-habana-cream focus:outline-none focus:border-gold";

    const save = async () => {
        try {
            if (editingId) {
                await api.put(`/categories/${editingId}`, draft);
                toast.success("Categoría actualizada");
            } else {
                await api.post("/categories", draft);
                toast.success("Categoría añadida");
            }
            setDraft(EMPTY_CAT);
            setEditingId(null);
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };
    const del = async (id) => {
        if (!window.confirm(lang === "es" ? "¿Eliminar categoría?" : "Delete category?")) return;
        try {
            await api.delete(`/categories/${id}`);
            toast.success(lang === "es" ? "Eliminada" : "Deleted");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="grid md:grid-cols-3 gap-6" data-testid="categories-pane">
            <div className="md:col-span-2 space-y-3">
                {cats.map((c) => (
                    <div key={c.id} className="flex items-center justify-between bg-habana-surface border border-gold/15 rounded-lg px-4 py-3">
                        <div>
                            <div className="font-serif text-lg text-habana-cream">{c.name_es} <span className="text-habana-cream/40">/ {c.name_en}</span></div>
                            <div className="text-xs text-habana-cream/50 tracking-widest uppercase mt-0.5">{c.slug}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="text-gold text-sm" onClick={() => { setEditingId(c.id); setDraft(c); }} data-testid={`cat-edit-${c.slug}`}>{t("admin_edit")}</button>
                            <button className="text-habana-red text-sm" onClick={() => del(c.id)} data-testid={`cat-del-${c.slug}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-habana-surface border border-gold/15 rounded-lg p-5">
                <div className="text-xs tracking-widest uppercase text-gold mb-3">{editingId ? t("admin_edit") : t("admin_add")}</div>
                <div className="space-y-3">
                    <Field label="Slug (unique)"><input className={inputCls} value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase() })} data-testid="cat-slug" /></Field>
                    <Field label="Nombre (ES)"><input className={inputCls} value={draft.name_es} onChange={(e) => setDraft({ ...draft, name_es: e.target.value })} data-testid="cat-name-es" /></Field>
                    <Field label="Name (EN)"><input className={inputCls} value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} data-testid="cat-name-en" /></Field>
                    <Field label="Order"><input type="number" className={inputCls} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} data-testid="cat-order" /></Field>
                    <div className="flex gap-2 pt-2">
                        <button className="btn-gold !py-2 !text-xs" onClick={save} data-testid="cat-save-btn"><Save size={12} />{t("admin_save")}</button>
                        {editingId && <button className="btn-ghost-gold !py-2 !text-xs" onClick={() => { setEditingId(null); setDraft(EMPTY_CAT); }} data-testid="cat-cancel-btn">{t("admin_cancel")}</button>}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* --------------------------------- Menu ---------------------------------- */
function MenuPane({ items, cats, onChange }) {
    const { t, lang } = useI18n();
    const [draft, setDraft] = useState({ ...EMPTY_ITEM, category: cats[0]?.slug || "cafeteria" });
    const [editingId, setEditingId] = useState(null);
    const inputCls = "w-full bg-black/40 border border-gold/25 rounded-md px-3 py-2 text-habana-cream focus:outline-none focus:border-gold";

    const save = async () => {
        try {
            if (editingId) {
                await api.put(`/menu/${editingId}`, draft);
                toast.success(lang === "es" ? "Plato actualizado" : "Item updated");
            } else {
                await api.post("/menu", draft);
                toast.success(lang === "es" ? "Plato añadido" : "Item added");
            }
            setDraft({ ...EMPTY_ITEM, category: cats[0]?.slug || "cafeteria" });
            setEditingId(null);
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };
    const del = async (id) => {
        if (!window.confirm(lang === "es" ? "¿Eliminar este plato?" : "Delete this item?")) return;
        try {
            await api.delete(`/menu/${id}`);
            toast.success(lang === "es" ? "Eliminado" : "Deleted");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="grid md:grid-cols-5 gap-6" data-testid="menu-pane">
            <div className="md:col-span-3 space-y-3 max-h-[75vh] overflow-y-auto pr-2">
                {items.map((it) => (
                    <div key={it.id} className="bg-habana-surface border border-gold/15 rounded-lg p-4 flex gap-4">
                        {it.image_url && <img src={it.image_url} alt="" className="w-16 h-16 rounded object-cover" />}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-serif text-lg text-habana-cream truncate">{it.name_es} <span className="text-habana-cream/40 text-sm">/ {it.name_en}</span></div>
                                <div className="text-gold font-serif whitespace-nowrap">{it.price}</div>
                            </div>
                            <div className="text-[0.65rem] uppercase tracking-widest text-gold/70 mt-0.5">{it.category}{it.featured ? " · ★" : ""}{!it.active ? " · oculto" : ""}</div>
                            <p className="text-sm text-habana-cream/60 mt-1 line-clamp-2">{it.description_es}</p>
                            <div className="mt-2 flex gap-3">
                                <button className="text-gold text-sm" onClick={() => { setEditingId(it.id); setDraft(it); }} data-testid={`menu-edit-${it.id}`}>{t("admin_edit")}</button>
                                <button className="text-habana-red text-sm" onClick={() => del(it.id)} data-testid={`menu-del-${it.id}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="md:col-span-2 bg-habana-surface border border-gold/15 rounded-lg p-5 space-y-3 max-h-[75vh] overflow-y-auto">
                <div className="text-xs tracking-widest uppercase text-gold">{editingId ? t("admin_edit") : t("admin_add")}</div>
                <Field label="Categoría">
                    <select className={inputCls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="menu-cat-select">
                        {cats.map((c) => (
                            <option key={c.slug} value={c.slug}>{c.name_es}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Nombre (ES)"><input className={inputCls} value={draft.name_es} onChange={(e) => setDraft({ ...draft, name_es: e.target.value })} data-testid="menu-name-es" /></Field>
                <Field label="Name (EN)"><input className={inputCls} value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} data-testid="menu-name-en" /></Field>
                <Field label="Descripción (ES)"><textarea rows={2} className={inputCls} value={draft.description_es} onChange={(e) => setDraft({ ...draft, description_es: e.target.value })} data-testid="menu-desc-es" /></Field>
                <Field label="Description (EN)"><textarea rows={2} className={inputCls} value={draft.description_en} onChange={(e) => setDraft({ ...draft, description_en: e.target.value })} data-testid="menu-desc-en" /></Field>
                <Field label="Precio"><input className={inputCls} value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="menu-price" placeholder="$12.00" /></Field>
                <Field label="Image URL"><input className={inputCls} value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} data-testid="menu-image" /></Field>
                <Field label="Orden"><input type="number" className={inputCls} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} data-testid="menu-order" /></Field>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-habana-cream/70">
                        <input type="checkbox" checked={draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} data-testid="menu-featured" />
                        Featured
                    </label>
                    <label className="flex items-center gap-2 text-sm text-habana-cream/70">
                        <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="menu-active" />
                        Activo
                    </label>
                </div>
                <div className="flex gap-2 pt-2">
                    <button className="btn-gold !py-2 !text-xs" onClick={save} data-testid="menu-save-btn"><Save size={12} />{t("admin_save")}</button>
                    {editingId && <button className="btn-ghost-gold !py-2 !text-xs" onClick={() => { setEditingId(null); setDraft({ ...EMPTY_ITEM, category: cats[0]?.slug || "cafeteria" }); }} data-testid="menu-cancel-btn">{t("admin_cancel")}</button>}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------- Gallery -------------------------------- */
function GalleryPane({ images, onChange }) {
    const { t, lang } = useI18n();
    const [draft, setDraft] = useState(EMPTY_GAL);
    const [editingId, setEditingId] = useState(null);
    const inputCls = "w-full bg-black/40 border border-gold/25 rounded-md px-3 py-2 text-habana-cream focus:outline-none focus:border-gold";

    const save = async () => {
        try {
            if (editingId) {
                await api.put(`/gallery/${editingId}`, draft);
                toast.success(lang === "es" ? "Imagen actualizada" : "Image updated");
            } else {
                await api.post("/gallery", draft);
                toast.success(lang === "es" ? "Imagen añadida" : "Image added");
            }
            setDraft(EMPTY_GAL);
            setEditingId(null);
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };
    const del = async (id) => {
        if (!window.confirm(lang === "es" ? "¿Eliminar imagen?" : "Delete image?")) return;
        try {
            await api.delete(`/gallery/${id}`);
            toast.success(lang === "es" ? "Eliminada" : "Deleted");
            onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="grid md:grid-cols-3 gap-6" data-testid="gallery-pane">
            <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {images.map((im) => (
                    <div key={im.id} className="relative group border border-gold/15 rounded-lg overflow-hidden">
                        <img src={im.url} alt="" className="w-full h-32 object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                            <button className="text-gold text-sm" onClick={() => { setEditingId(im.id); setDraft(im); }} data-testid={`gal-edit-${im.id}`}>{t("admin_edit")}</button>
                            <button className="text-habana-red text-sm" onClick={() => del(im.id)} data-testid={`gal-del-${im.id}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-habana-surface border border-gold/15 rounded-lg p-5 space-y-3">
                <div className="text-xs tracking-widest uppercase text-gold">{editingId ? t("admin_edit") : t("admin_add")}</div>
                <Field label="Image URL"><input className={inputCls} value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} data-testid="gal-url" /></Field>
                <Field label="Caption (ES)"><input className={inputCls} value={draft.caption_es} onChange={(e) => setDraft({ ...draft, caption_es: e.target.value })} data-testid="gal-caption-es" /></Field>
                <Field label="Caption (EN)"><input className={inputCls} value={draft.caption_en} onChange={(e) => setDraft({ ...draft, caption_en: e.target.value })} data-testid="gal-caption-en" /></Field>
                <Field label="Orden"><input type="number" className={inputCls} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} data-testid="gal-order" /></Field>
                {draft.url && <img src={draft.url} alt="" className="w-full h-32 object-cover rounded" />}
                <div className="flex gap-2 pt-2">
                    <button className="btn-gold !py-2 !text-xs" onClick={save} data-testid="gal-save-btn"><Save size={12} />{t("admin_save")}</button>
                    {editingId && <button className="btn-ghost-gold !py-2 !text-xs" onClick={() => { setEditingId(null); setDraft(EMPTY_GAL); }} data-testid="gal-cancel-btn">{t("admin_cancel")}</button>}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------- Helpers -------------------------------- */
function SectionCard({ title, children }) {
    return (
        <div className="bg-habana-surface border border-gold/15 rounded-xl p-5 md:p-6">
            <div className="font-serif text-lg text-gold mb-4">{title}</div>
            {children}
        </div>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <div className="text-[0.65rem] tracking-widest uppercase text-gold/80 mb-1">{label}</div>
            {children}
        </div>
    );
}
